import type { ItemTuple, RoleSource } from '../../types/framework'

/*
 * Every board role in the source workbook uses the same twelve statement
 * templates per domain, with the domain name (lower-cased) inserted, and
 * item numbers running sequentially: 6 Quality then 6 Behaviour per domain.
 * Generating them from the templates reproduces the source text exactly.
 */

const CHARACTERISTIC_TEMPLATES: [string, (topic: string) => string][] = [
  ['Integrity and Ethical Consistency', (t) => `Demonstrates integrity and ethical consistency when addressing ${t}.`],
  ['Balanced Judgement', (t) => `Applies sound, balanced judgement to matters relating to ${t}.`],
  ['Objectivity and Evidence-Based Judgement', (t) => `Forms an evidence-based view on ${t} without undue influence from personal or external interests.`],
  ['Constructive Courage', (t) => `Shows the confidence to raise difficult or unpopular questions about ${t}.`],
  ['Systems and Stakeholder Perspective', (t) => `Understands the wider organisational and stakeholder implications of ${t}.`],
  ['Self-Awareness and Learning Agility', (t) => `Recognises the limits of personal knowledge and seeks appropriate expertise on ${t}.`],
]

const BEHAVIOUR_TEMPLATES: [string, (topic: string) => string][] = [
  ['Preparation and Evidence Review', (t) => `Reviews relevant papers, evidence and options before making decisions about ${t}.`],
  ['Constructive Challenge', (t) => `Asks probing, respectful questions that test assumptions and expose gaps concerning ${t}.`],
  ['Reasoned Decision-Making', (t) => `Makes or supports clear, reasoned decisions on ${t} and records the rationale where appropriate.`],
  ['Monitoring and Follow-Through', (t) => `Tracks agreed actions, indicators and emerging issues relating to ${t}.`],
  ['Collaboration', (t) => `Works constructively with fellow directors and relevant leaders to address ${t}.`],
  ['Clear and Timely Communication', (t) => `Communicates concerns, conclusions and recommendations about ${t} clearly and in a timely manner.`],
]

function boardRole(
  id: string,
  name: string,
  firstNo: number,
  domainNames: string[],
): RoleSource {
  return {
    id,
    name,
    group: 'Board',
    domains: domainNames.map((domainName, i) => {
      const start = firstNo + i * 12
      const topic = domainName.toLowerCase()
      const characteristic: ItemTuple[] = CHARACTERISTIC_TEMPLATES.map(
        ([label, text], j) => [start + j, label, text(topic)],
      )
      const behaviour: ItemTuple[] = BEHAVIOUR_TEMPLATES.map(
        ([label, text], j) => [start + 6 + j, label, text(topic)],
      )
      return { name: domainName, characteristic, behaviour }
    }),
  }
}

export const boardChair = boardRole('board-chair', 'Board Chairperson', 901, [
  'Board Leadership and Effectiveness',
  'Strategic Direction and Long-Term Value',
  'Governance Framework and Accountability',
  'Board Agenda, Information and Decision Quality',
  'CEO Relationship and Executive Oversight',
  'Board Culture, Dynamics and Constructive Challenge',
  'Stakeholder Engagement and Representation',
  'Ethics, Integrity and Tone at the Top',
  'Risk Oversight and Resilience',
  'Performance Evaluation and Board Renewal',
  'Crisis Leadership and Reputation Stewardship',
  'Succession Planning and Organisational Continuity',
])

export const ned = boardRole('ned', 'Non-Executive Director', 1045, [
  'Strategic Contribution and Business Acumen',
  'Fiduciary Duties and Governance',
  'Independent Judgement and Constructive Challenge',
  'Financial Literacy and Performance Oversight',
  'Risk Management and Internal Control',
  'Ethics, Integrity and Conflicts of Interest',
  'Executive Accountability and CEO Oversight',
  'Stakeholder and Shareholder Awareness',
  'Boardroom Collaboration and Team Contribution',
  'Preparation, Attendance and Engagement',
  'Sustainability and Long-Term Value Creation',
  'Learning, Development and Board Effectiveness',
])

export const ined = boardRole('ined', 'Independent Non-Executive Director', 1189, [
  'Independence of Mind and Objectivity',
  'Conflict Identification and Management',
  'Constructive Challenge and Professional Scepticism',
  'Fiduciary Duties and Minority-Interest Protection',
  'Governance Standards and Regulatory Awareness',
  'Financial and Non-Financial Performance Oversight',
  'Risk, Controls and Assurance',
  'Related-Party Transactions and Fairness',
  'Stakeholder Balance and Ethical Judgement',
  'Confidentiality and Information Stewardship',
  'Boardroom Influence Without Executive Authority',
  'Continuous Learning and Independence Safeguards',
])

export const auditChair = boardRole('audit-chair', 'Audit Committee Chair', 1333, [
  'Financial Reporting Integrity',
  'Audit Committee Leadership and Agenda',
  'Internal and External Audit Oversight',
  'Internal Controls and Control Environment',
  'Enterprise Risk and Assurance Coordination',
  'Accounting Judgement and Estimates',
  'Fraud, Misconduct and Whistleblowing',
  'Regulatory Compliance and Disclosure',
  'Auditor Independence and Audit Quality',
  'Cybersecurity, Data and Technology Controls',
  'Committee Communication and Board Reporting',
  'Professional Scepticism and Follow-Through',
])

export const nominationsChair = boardRole('nominations-chair', 'Nominations Committee Chair', 1477, [
  'Board Composition and Skills Planning',
  'Director Search and Candidate Assessment',
  'Independence, Diversity and Inclusion',
  'CEO and Executive Succession Planning',
  'Board and Committee Evaluation',
  'Director Induction and Development',
  'Governance Policy and Appointment Process',
  'Leadership Pipeline and Talent Oversight',
  'Stakeholder Engagement on Board Renewal',
  'Ethics, Conflicts and Candidate Due Diligence',
  'Committee Leadership and Decision Quality',
  'Confidentiality and Sensitive Information',
])

export const nfpDirector = boardRole('nfp-director', 'Not-for-Profit Director', 1621, [
  'Mission, Purpose and Public Benefit',
  'Fiduciary Duties and Stewardship of Resources',
  'Strategy and Impact Oversight',
  'Financial Sustainability and Budget Oversight',
  'Fundraising, Donor Relations and Grant Stewardship',
  'Beneficiary and Community Accountability',
  'Risk Management, Safeguarding and Duty of Care',
  'Ethics, Transparency and Conflicts of Interest',
  'Regulatory Compliance and Charity Governance',
  'Executive Leadership and CEO Oversight',
  'Board Collaboration and Volunteer Governance',
  'Impact Measurement, Learning and Accountability',
])
